import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import api from "@/api/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Warehouse, Truck, FileText, Printer, IndianRupee, Package2, Filter, X } from "lucide-react";

const money = (v) => "₹" + Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

/**
 * ReleaseHistory — audit-friendly report of every quarantine → storage/bowser
 * release, one row per destination. Answers "where did each drop of that batch
 * end up?" for month-end reconciliation, regulatory audits, and stock disputes.
 */
export default function ReleaseHistory() {
  const [filters, setFilters] = useState({ date_from: "", date_to: "", dest_kind: "", vendor: "", po_code: "" });

  const qs = useMemo(() => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) p.append(k, v); });
    return p.toString();
  }, [filters]);

  const { data, isFetching } = useQuery({
    queryKey: ["release-history", qs],
    queryFn: () => api.get(`/procurement/release-history${qs ? `?${qs}` : ""}`).then((r) => r.data),
  });

  const rows = data?.items || [];
  const activeFilters = Object.entries(filters).filter(([, v]) => v);

  const exportCsv = () => {
    const header = ["Date/Time", "PO", "Vendor", "Batch", "Destination Kind", "Destination Code", "Quantity (L)", "Rate", "Value (₹)", "Loading Invoice", "By"];
    const csv = [header.join(",")].concat(
      rows.map((r) => [
        r.at, r.po_code, `"${(r.vendor || "").replace(/"/g, '""')}"`, r.batch,
        r.dest_kind, r.dest_code, r.quantity, r.rate, r.value,
        r.loading_invoice_code, r.by,
      ].join(",")),
    ).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `release-history-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
  };

  return (
    <div className="space-y-4" data-testid="release-history-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-black tracking-tight">Quarantine Release History</h1>
          <p className="text-sm text-muted-foreground">
            Every batch → storage / bowser release, chronologically. Use filters for audits &amp; month-end reconciliation.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportCsv} data-testid="btn-export-csv" disabled={rows.length === 0}>
            <FileText size={14} className="mr-1" /> Export CSV
          </Button>
          <Button variant="outline" onClick={() => window.print()} data-testid="btn-print-report">
            <Printer size={14} className="mr-1" /> Print
          </Button>
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Kpi icon={Package2} label="Releases" value={data?.count || 0} />
        <Kpi icon={Warehouse} label="To tanks" value={`${(data?.tank_quantity || 0).toLocaleString("en-IN")} L`} />
        <Kpi icon={Truck} label="To bowsers" value={`${(data?.bowser_quantity || 0).toLocaleString("en-IN")} L`} />
        <Kpi icon={IndianRupee} label="Value released" value={money(data?.total_value)} />
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="border-b border-border/60 py-3">
          <CardTitle className="flex items-center gap-2 text-sm uppercase tracking-widest text-muted-foreground">
            <Filter size={14} /> Filters
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-2 lg:grid-cols-5">
          <div>
            <Label className="text-[10px]">From date</Label>
            <Input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} data-testid="rh-date-from" />
          </div>
          <div>
            <Label className="text-[10px]">To date</Label>
            <Input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} data-testid="rh-date-to" />
          </div>
          <div>
            <Label className="text-[10px]">Destination kind</Label>
            <Select value={filters.dest_kind || "__all__"} onValueChange={(v) => setFilters({ ...filters, dest_kind: v === "__all__" ? "" : v })}>
              <SelectTrigger data-testid="rh-dest-kind"><SelectValue placeholder="Any" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Any</SelectItem>
                <SelectItem value="tank">Tank</SelectItem>
                <SelectItem value="bowser">Bowser</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px]">Vendor</Label>
            <Input value={filters.vendor} onChange={(e) => setFilters({ ...filters, vendor: e.target.value })} placeholder="Exact vendor name" data-testid="rh-vendor" />
          </div>
          <div>
            <Label className="text-[10px]">PO code</Label>
            <Input value={filters.po_code} onChange={(e) => setFilters({ ...filters, po_code: e.target.value })} placeholder="PO-0001" data-testid="rh-po-code" />
          </div>
          {activeFilters.length > 0 && (
            <div className="flex items-end sm:col-span-2 lg:col-span-5">
              <Button
                variant="ghost" size="sm"
                onClick={() => setFilters({ date_from: "", date_to: "", dest_kind: "", vendor: "", po_code: "" })}
                data-testid="btn-clear-filters"
              >
                <X size={12} className="mr-1" /> Clear filters ({activeFilters.length})
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="data-table w-full">
              <thead className="border-b border-border/60 bg-muted/40 text-left text-xs uppercase tracking-widest text-muted-foreground">
                <tr>
                  <th className="px-4 py-3">When</th>
                  <th className="px-4 py-3">PO</th>
                  <th className="px-4 py-3">Vendor</th>
                  <th className="px-4 py-3">Batch</th>
                  <th className="px-4 py-3">Destination</th>
                  <th className="px-4 py-3 text-right">Qty (L)</th>
                  <th className="px-4 py-3 text-right">Rate</th>
                  <th className="px-4 py-3 text-right">Value</th>
                  <th className="px-4 py-3">Loading Inv</th>
                  <th className="px-4 py-3">By</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={`${r.po_id}-${i}`} className="border-b border-border/40 text-sm" data-testid={`rh-row-${i}`}>
                    <td className="px-4 py-3 whitespace-nowrap text-xs">
                      <div>{(r.at || "").slice(0, 10)}</div>
                      <div className="text-[10px] text-muted-foreground">{(r.at || "").slice(11, 19)}</div>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs font-semibold">{r.po_code}</td>
                    <td className="px-4 py-3">
                      <div className="text-sm font-semibold">{r.vendor}</div>
                      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{r.vendor_type}</div>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">{r.batch || "—"}</td>
                    <td className="px-4 py-3">
                      <Badge className={r.dest_kind === "tank" ? "bg-blue-100 text-blue-700" : "bg-amber-100 text-amber-800"}>
                        {r.dest_kind === "tank" ? <Warehouse size={9} className="mr-1 inline" /> : <Truck size={9} className="mr-1 inline" />}
                        {(r.dest_kind || "").toUpperCase()}
                      </Badge>
                      <div className="mt-0.5 font-mono text-xs">{r.dest_code}</div>
                    </td>
                    <td className="px-4 py-3 text-right font-mono">{Number(r.quantity).toLocaleString("en-IN")}</td>
                    <td className="px-4 py-3 text-right font-mono text-xs">₹{r.rate}</td>
                    <td className="px-4 py-3 text-right font-mono font-bold">{money(r.value)}</td>
                    <td className="px-4 py-3 font-mono text-xs">
                      {r.loading_invoice_code ? (
                        <Link to="/app/loading-invoices" className="text-primary hover:underline">{r.loading_invoice_code}</Link>
                      ) : "—"}
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">{r.by || "—"}</td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={10} className="px-4 py-10 text-center text-sm text-muted-foreground">
                      <Package2 size={26} className="mx-auto mb-2 opacity-40" />
                      {isFetching ? "Loading…" : "No releases match your filters."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

const Kpi = ({ icon: Icon, label, value }) => (
  <div className="kpi-card">
    <div className="flex items-center justify-between text-muted-foreground">
      <div className="text-xs uppercase tracking-widest">{label}</div>
      <Icon size={14} />
    </div>
    <div className="mt-2 font-display text-xl font-black">{value}</div>
  </div>
);
